import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useSearchParams } from 'react-router-dom';
import { Star, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ value, onPick }) => (
  <div className="flex items-center justify-center gap-1.5" data-testid="rating-stars">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onPick(n)}
        aria-label={`${n} star${n > 1 ? 's' : ''}`}
        data-testid={`rating-star-${n}`}
        className="rounded-full p-1 transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold"
      >
        <Star
          className={`h-10 w-10 transition-colors ${
            n <= value ? 'fill-gold text-gold' : 'text-slate-300'
          }`}
        />
      </button>
    ))}
  </div>
);

// The customer's page from the review email. No login, one job, one rating.
export const RatePage = () => {
  const { token } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [stars, setStars] = useState(Number(params.get('stars')) || 0);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/rating/${token}`)
      .then((res) => {
        setData(res.data);
        if (res.data?.already) {
          setStars(res.data.already.stars);
          setComment(res.data.already.comment || '');
        }
      })
      .catch((err) => setError(err?.response?.data?.detail || 'We could not find that rating link'));
  }, [token]);

  const submit = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/public/rating/${token}`, { stars, comment });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Sorry, that did not save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const reviewLink = data?.review_link;

  return (
    <div className="min-h-screen bg-page" data-testid="rate-page">
      <header className="bg-ink px-5 py-6 text-center">
        <img
          src="/butler-direct-logo-horizontal-dark.png"
          alt="Butler Direct"
          className="mx-auto h-9 w-auto"
        />
      </header>

      <main className="mx-auto max-w-xl px-5 py-10">
        {error && !data && (
          <p className="rounded-[12px] border border-hair bg-white p-5 text-center text-ink"
             data-testid="rate-error">{error}</p>
        )}

        {data && (
          <div className="rounded-[12px] border border-hair bg-white p-6 shadow-sm sm:p-8">
            <p className="bd-heading text-[11px] text-gold-deep">How did we do?</p>

            {!done ? (
              <>
                <h1 className="mt-2 text-2xl font-extrabold leading-tight text-ink sm:text-3xl">
                  {data.job.first_name ? `Thanks, ${data.job.first_name}!` : 'Thanks!'}
                </h1>
                <p className="mt-3 text-sm text-slate-600" data-testid="rate-job-details">
                  You're rating <strong className="text-ink">{data.job.service}</strong> on{' '}
                  <strong className="text-ink">{data.job.date_long}</strong>
                  {data.job.host ? <> with <strong className="text-ink">{data.job.host}</strong></> : null}.
                </p>

                <div className="mt-7">
                  <Stars value={stars} onPick={setStars} />
                  <p className="mt-2 text-center text-xs text-slate-500">
                    Tap a star, from one to five.
                  </p>
                </div>

                <div className="mt-6">
                  <label className="mb-1.5 block text-xs font-semibold text-slate-600">
                    Anything you'd like to add? (optional)
                  </label>
                  <Textarea
                    rows={4}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="What went well, or what we could do better…"
                    data-testid="rate-comment"
                  />
                </div>

                {error && (
                  <p className="mt-3 text-sm text-red-600" data-testid="rate-save-error">{error}</p>
                )}

                <Button
                  className="mt-5 w-full"
                  disabled={!stars || saving}
                  onClick={submit}
                  data-testid="rate-submit"
                >
                  {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Send my rating
                </Button>
              </>
            ) : (
              <div data-testid="rate-thanks">
                <h1 className="mt-2 flex items-center gap-2 text-2xl font-extrabold text-ink">
                  <CheckCircle2 className="h-6 w-6 text-gold-deep" /> Thank you
                </h1>
                <p className="mt-3 text-sm text-slate-600">
                  That means a lot to us, and we've passed it straight on to the team.
                </p>
              </div>
            )}

            {reviewLink && (
              <div className="mt-8 border-t border-hair pt-6 text-center">
                <p className="text-sm text-slate-600">
                  Would you share it publicly too? It really helps a small business like ours.
                </p>
                <Button asChild className="mt-3 w-full" data-testid="rate-trustpilot">
                  <a href={reviewLink} target="_blank" rel="noreferrer">Leave us a review</a>
                </Button>
              </div>
            )}
          </div>
        )}

        <p className="mt-6 text-center text-xs text-slate-500">
          Butler Direct · bookings@butlerdirect.co.uk
        </p>
      </main>
    </div>
  );
};

export default RatePage;
